import { ReactNode, useMemo } from 'react'
import { z } from 'zod'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { ColumnDef } from '@tanstack/react-table'
import { Trash2, Undo2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import BannerImage from 'app/frontend/base/components/BannerImage'
import SelectField from 'app/frontend/base/components/form-fields/SelectField'
import { useGuild } from 'app/frontend/base/config/context/guild'
import { hasAdminAccessToAllTeams } from 'app/frontend/base/config/permissions'
import { useGuildLinkCandidates } from 'app/frontend/base/config/queries'
import NewsTagBadge from 'app/frontend/base/pages/News/NewsTagBadge'

export const linkedGuildRowsSchema = z.array(
  z.object({
    linkedGuildId: z.string().nullable(),
    persisted: z.boolean().optional(),
    _destroy: z.boolean().optional(),
  }),
)

export type LinkedGuildRow = z.infer<typeof linkedGuildRowsSchema>[number]

export const linkedGuildRowsFor = (guild: wowaudit.Guild): LinkedGuildRow[] =>
  guild.linkedGuilds.map((linkedGuild) => ({
    linkedGuildId: linkedGuild.id.toString(),
    persisted: true,
  }))

export const linkedGuildIdsFromRows = (rows: LinkedGuildRow[]) =>
  rows
    .filter((row) => !row._destroy && row.linkedGuildId)
    .map((row) => Number(row.linkedGuildId))

const name = 'linkedGuildRows'

type IndexedRow = LinkedGuildRow & {
  _index: number
  linkedGuild?: wowaudit.LinkedGuild
}

const linkedGuildColumn = (
  id: string,
  header: string,
  render: (linkedGuild: wowaudit.LinkedGuild) => ReactNode,
): ColumnDef<IndexedRow> => ({
  id,
  header,
  cell: ({ row }) =>
    row.original.linkedGuild ? (
      <div className={`mt-2 ${row.original._destroy ? 'opacity-30' : ''}`}>
        {render(row.original.linkedGuild)}
      </div>
    ) : null,
})

const LinkedGuildsTable = () => {
  const { guild } = useGuild()
  const { watch, setValue } = useFormContext()
  const { append, remove } = useFieldArray({ name })
  const rows: LinkedGuildRow[] = watch(name) ?? []
  const readonly = !hasAdminAccessToAllTeams(guild)
  const { data: candidates = [] } = useGuildLinkCandidates(guild?.id, !readonly)

  const guildsById = useMemo(
    () =>
      new Map(
        [...(guild?.linkedGuilds ?? []), ...candidates].map((linkedGuild) => [
          linkedGuild.id.toString(),
          linkedGuild,
        ]),
      ),
    [guild?.linkedGuilds, candidates],
  )

  const indexedRows = rows.map((row, index) => ({
    ...row,
    _index: index,
    linkedGuild: row.linkedGuildId
      ? guildsById.get(row.linkedGuildId)
      : undefined,
  }))

  const columns: ColumnDef<IndexedRow>[] = useMemo(
    () => [
      {
        id: 'guild',
        header: 'Guild',
        cell: ({ row }) => (
          <SelectField
            layout='stacked'
            name={`${name}.${row.original._index}.linkedGuildId`}
            options={[...guildsById.values()]
              .filter(
                (linkedGuild) =>
                  linkedGuild.id.toString() === row.original.linkedGuildId ||
                  !rows.some(
                    (other) =>
                      other.linkedGuildId === linkedGuild.id.toString(),
                  ),
              )
              .map((linkedGuild) => ({
                label: (
                  <span className='flex items-center gap-3'>
                    <BannerImage
                      size='custom'
                      className='w-10 h-10 min-w-10 min-h-10 rounded-md'
                      faction={linkedGuild.faction}
                      fileName={linkedGuild.bannerFilename}
                    />
                    <span className='flex min-w-0 flex-col'>
                      <span className='truncate font-medium'>
                        {linkedGuild.name}
                      </span>
                      <span className='truncate text-xs text-muted-foreground'>
                        {linkedGuild.realm.nameWithRegion}
                      </span>
                    </span>
                    <NewsTagBadge
                      tag={linkedGuild.gameVersion}
                      className='ml-auto shrink-0'
                    />
                  </span>
                ),
                triggerLabel: (
                  <span className='flex items-center gap-2'>
                    <BannerImage
                      size='custom'
                      className='w-5 h-5 min-w-5 min-h-5 rounded-sm'
                      faction={linkedGuild.faction}
                      fileName={linkedGuild.bannerFilename}
                    />
                    <span className='min-w-0 truncate'>{linkedGuild.name}</span>
                  </span>
                ),
                sortLabel: linkedGuild.name,
                value: linkedGuild.id.toString(),
              }))}
            transformOptions={false}
            disabled={!!row.original._destroy || readonly}
            className={`w-80 max-w-full ${
              row.original._destroy ? 'opacity-30' : ''
            }`}
            customLabel={''}
            customPlaceholder='Select guild'
          />
        ),
      },
      linkedGuildColumn(
        'realm',
        'Realm',
        (linkedGuild) => linkedGuild.realm.name,
      ),
      linkedGuildColumn(
        'region',
        'Region',
        (linkedGuild) => linkedGuild.realm.region,
      ),
      linkedGuildColumn('game_version', 'Game version', (linkedGuild) => (
        <NewsTagBadge tag={linkedGuild.gameVersion} />
      )),
      {
        id: 'actions',
        header: '',
        size: 50,
        cell: ({ row }) => (
          <div className='flex justify-end mt-0.5'>
            <Button
              type='button'
              size='sm'
              disabled={readonly}
              variant={row.original._destroy ? 'secondary' : 'destructive'}
              onClick={() =>
                row.original.persisted
                  ? setValue(
                      `${name}.${row.original._index}._destroy`,
                      !row.original._destroy,
                      { shouldDirty: true },
                    )
                  : remove(row.original._index)
              }
            >
              {row.original._destroy ? (
                <Undo2 size={14} />
              ) : (
                <Trash2 size={14} />
              )}
            </Button>
          </div>
        ),
      },
    ],
    [guildsById, readonly, remove, rows, setValue],
  )

  return (
    <div>
      <DataTable
        cellClassName='align-top px-2'
        includeHeader={indexedRows.length > 0}
        columns={columns}
        data={indexedRows}
        emptyMessage={() => 'No linked guilds'}
      />

      {readonly ? null : (
        <div className='flex justify-end'>
          <Button
            type='button'
            size='sm'
            className='mt-2'
            variant='outline'
            onClick={() => append({ linkedGuildId: null })}
          >
            Add linked guild
          </Button>
        </div>
      )}
    </div>
  )
}

export default LinkedGuildsTable
